import * as React from 'react';
import {A11y, Navigation, Pagination, Scrollbar} from 'swiper';
import {faCheckCircle} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Swiper, SwiperSlide} from 'swiper/react';

import {gaLegacyCustomEvent} from '../../../client/ga/ga-legacy.functions';
import {ProductImage} from '../../ui/ProductImage/ProductImage';
import {pushGaEvent} from '../../../client/ga/ga.functions';
import {SearchResult} from '../search.types';
import {SerializedURLBuilder, URLBuilder} from '../../../server/tools/url-builder/url-builder.class';
import {TemplateHelpers} from '../../tools/template-helpers.class';
import {swiperSlideContent, swiperSlideStyles} from '../../ui/Swiper/SwiperClasses';

interface GuidedNavProps {
    gaCategoryPath: string;
    resultList: SearchResult;
    serializedURLBuilder: SerializedURLBuilder;
}

export const GuidedNav = ({gaCategoryPath, resultList, serializedURLBuilder}: GuidedNavProps) => {
    const buildURL = URLBuilder.fromSerialized(serializedURLBuilder);

    /**
     * Template
     */
    return (
        <div
            className="tw-min-h-[160px]"
            data-e2e="GuidedNav"
        >
            <Swiper
                breakpoints={{
                    320: {
                        slidesPerGroup: 2,
                        slidesPerView: 2,
                        spaceBetween: 20,
                    },
                    480: {
                        slidesPerGroup: 3,
                        slidesPerView: 3,
                        spaceBetween: 30,
                    },
                    600: {
                        slidesPerGroup: 4,
                        slidesPerView: 4,
                        spaceBetween: 40,
                    },
                    800: {
                        slidesPerGroup: 5,
                        slidesPerView: 5,
                        spaceBetween: 40,
                    },
                    1000: {
                        slidesPerGroup: 6,
                        slidesPerView: 6,
                        spaceBetween: 40,
                    },
                }}
                className={`${swiperSlideStyles} ${swiperSlideContent}`}
                modules={[Navigation, Pagination, Scrollbar, A11y]}
                navigation
                onSlideNextTransitionEnd={() => {
                    gaLegacyCustomEvent({
                        eventAction: `Next Click`,
                        eventCategory: `Guided Navigation`,
                        eventLabel: window.location.href,
                    });
                }}
                onSlidePrevTransitionEnd={() => {
                    gaLegacyCustomEvent({
                        eventAction: `Previous Click`,
                        eventCategory: `Guided Navigation`,
                        eventLabel: window.location.href,
                    });
                }}
                watchSlidesProgress={true}
            >
                {resultList.guidedNavFacets?.facets?.length > 0 &&
                    resultList.guidedNavFacets.facets.map((facet, i) => (
                        <SwiperSlide key={`guidedFacet_${i}`}>
                            <a
                                className="tw-no-underline"
                                href={
                                    facet.checked
                                        ? buildURL.withoutFilter(facet.field, facet.value)
                                        : buildURL.swapFilter(facet.field, facet.value)
                                }
                                onClick={() => {
                                    gaLegacyCustomEvent({
                                        eventAction: `Guided Navigation Facet ${facet.checked ? 'Off' : 'On'}`,
                                        eventCategory: `Faceted Search`,
                                        eventLabel: `${gaCategoryPath} | ${facet.text?.trim()}`,
                                    });
                                    pushGaEvent(`ga4_guided_navigation`, {
                                        click_action: facet.checked ? `remove_facet` : `add_facet`,
                                        click_category_1: `guided_navigation_swiper`,
                                        click_category_2: `Subgroup`,
                                        click_path: buildURL.withoutFilter(facet.field, facet.value),
                                        click_type: `guided_navigation`,
                                        selection_name: `Subgroup | ${resultList.title}: ${facet.text}`,
                                    });
                                }}
                            >
                                <div className={`tw-relative tw-text-center`}>
                                    {facet.checked && (
                                        <FontAwesomeIcon
                                            className="tw-absolute tw-top-0 -tw-left-[15px]"
                                            color="#00599c"
                                            icon={faCheckCircle}
                                            size="2x"
                                        />
                                    )}
                                    <ProductImage
                                        altText={facet.text}
                                        className="tw-max-w-full tw-h-auto"
                                        size={96}
                                        src={facet.image}
                                        srcIncludesImgThumbsPath
                                    />
                                    <div className="tw-mt-4">{TemplateHelpers.truncate(facet.text, 65)}</div>
                                </div>
                            </a>
                        </SwiperSlide>
                    ))}
                {resultList.jumpToLinks?.length > 0 &&
                    resultList.jumpToLinks.map((jumpTo, i) => (
                        <SwiperSlide key={`jumpTo_${i}`}>
                            <a
                                className="tw-no-underline"
                                href={`/browse/${jumpTo.categoryPath}`}
                                onClick={() => {
                                    gaLegacyCustomEvent({
                                        eventAction: `Jump To Click`,
                                        eventCategory: `Faceted Search`,
                                        eventLabel: `${jumpTo.categoryPath} | ${jumpTo.text?.trim()}`,
                                    });
                                    pushGaEvent(`ga4_guided_navigation`, {
                                        click_action: `navigation`,
                                        click_category_1: `guided_navigation_swiper`,
                                        click_category_2: `jump_to_click`,
                                        click_path: `/browse/${jumpTo.categoryPath}`,
                                        click_type: `guided_navigation`,
                                        selection_name: jumpTo.text,
                                    });
                                }}
                            >
                                <div className="tw-relative tw-text-center">
                                    <ProductImage
                                        altText={jumpTo.text}
                                        className="tw-max-w-full tw-h-auto"
                                        size={96}
                                        src={jumpTo.image}
                                        srcIncludesImgThumbsPath
                                    />
                                    <div className="tw-mt-4">{TemplateHelpers.truncate(jumpTo.text, 65)}</div>
                                </div>
                            </a>
                        </SwiperSlide>
                    ))}
            </Swiper>
        </div>
    );
};
